import {
  type AdminCreateFreeProductRequest,
  type AdminCreateProductRequest,
  type AdminFreeProduct,
  type AdminProduct,
  type AdminProductCatalog,
  defaultFreeProductSignupFields,
  type SignupField,
  signupFieldsSchema,
  signupSlugSchema,
} from "@ternary/contracts"
import type { WizardErrors } from "@ternary/ui"

/** How members pay. A free product is a signup page; the paid ones sell through Stripe. */
export type PricingType = "free" | "one_time" | "recurring"

/**
 * The product wizard's draft: text inputs keep the text as typed, and the request builders turn
 * it into a §3.1 request. `persistKey` stores it as JSON, so it holds only plain values.
 */
export type ProductDraft = {
  name: string
  slug: string
  /** False while the slug still follows the name. */
  slugEdited: boolean
  description: string
  /** Kept from the saved product: PUT replaces the whole record. */
  imageUrl: string | null
  pricingType: PricingType
  /** A paid product's existing Stripe product, or "" for a new one. */
  existingProductId: string
  price: string
  currency: string
  interval: "day" | "week" | "month" | "year"
  intervalCount: string
  initialPayment: string
  trialDays: string
  /** A free product's Discord server, one of the catalog's proven guilds. */
  guildId: string
  /** The roles granted on join: a free product's from its server, a paid one's from the primary. */
  roleIds: string[]
  /** A free product that grants no role, for a server used only for tracking. */
  noRoles: boolean
  signupFields: SignupField[]
  productType: "discord" | "manual"
  trialRoleIds: string[]
  removal: "kick" | "roles"
  removalRoleIds: string[]
  /** One feature per line. */
  features: string
  stock: string
  upgradeTo: string[]
  addOns: string[]
}

export const SLUG_TAKEN = "That link is already used by another product."

// The contract reserves these (signupFieldsSchema): the details step always asks for them.
const RESERVED_FIELD_KEYS = ["firstname", "lastname", "email"]
const PAID_SLUG = /^[a-zA-Z0-9_-]{1,120}$/
const MONEY = /^\d{1,7}(\.\d{1,2})?$/
const WHOLE = /^\d{1,7}$/

/** Lower-case ASCII words, with accents folded, joined by `separator`. */
function words(text: string, separator: string) {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, separator)
    .replace(new RegExp(`^${separator}+|${separator}+$`, "g"), "")
}

/** The signup link a name suggests: `/^[a-z0-9-]{3,48}$/`, or "" when the name gives too little. */
export function slugFromName(name: string): string {
  const slug = words(name, "-").slice(0, 48).replace(/-+$/, "")
  return slug.length >= 3 ? slug : ""
}

/**
 * A field key for a label: `/^[a-z][a-z0-9_]{1,31}$/`, never one the details step asks for, and
 * not among `taken` (a clash appends _2, _3…).
 */
export function fieldKeyFromLabel(label: string, taken: readonly string[]): string {
  let base = words(label, "_")
  if (!/^[a-z]/.test(base) || base.length < 2) base = `field_${base}`.replace(/_+$/, "")
  base = base.slice(0, 32).replace(/_+$/, "")
  const blocked = new Set([...taken, ...RESERVED_FIELD_KEYS])
  if (!blocked.has(base)) return base
  for (let count = 2; ; count += 1) {
    const suffix = `_${count}`
    const key = `${base.slice(0, 32 - suffix.length).replace(/_+$/, "")}${suffix}`
    if (!blocked.has(key)) return key
  }
}

/** A new free product's fields (§3.1): phone, country and heard_about, as a fresh copy. */
export function defaultSignupFields(): SignupField[] {
  return structuredClone([...defaultFreeProductSignupFields])
}

/** The path of a free product's public signup page, before its slug. */
export const signupPathPrefix = "/signup/"

/** The public signup page of a free product with `slug`. */
export function signupUrl(storefrontUrl: string, slug: string): string {
  return new URL(`${signupPathPrefix}${slug}`, storefrontUrl).toString()
}

/** A paid product on the storefront: its root, with `?password=` for a password-only product. */
export function storefrontProductUrl(storefrontUrl: string, slug: string | null): string {
  const url = new URL("/", storefrontUrl)
  if (slug) url.searchParams.set("password", slug)
  return url.toString()
}

export function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { currency, style: "currency" }).format(amountMinor / 100)
}

/** The symbol `formatMoney` writes for a currency, such as "$", "€" or "CA$", for an amount's field. */
export function currencySymbol(currency: string): string {
  return (
    new Intl.NumberFormat("en-US", { currency, style: "currency" })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? currency
  )
}

/** "$29.00 every month", "$99.00 every 3 months" or "$49.00 once". */
export function paidPriceLabel(
  priceMinor: number,
  currency: string,
  interval: AdminProduct["interval"],
  intervalCount: number,
): string {
  const price = formatMoney(priceMinor, currency)
  if (interval === "one_time") return `${price} once`
  return intervalCount === 1
    ? `${price} every ${interval}`
    : `${price} every ${intervalCount} ${interval}s`
}

function minor(amount: string) {
  return Math.round(Number(amount.trim() || "0") * 100)
}

function whole(value: string) {
  return Number.parseInt(value.trim() || "0", 10)
}

/** The non-blank lines of a textarea, trimmed: what a features list sends. */
export function lines(text: string) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
}

/** A server the bot can reach: the catalog says `connected: false` when it cannot. */
export function isReachable(guild: AdminProductCatalog["guilds"][number]): boolean {
  return guild.connected !== false
}

/** A new product: free, on the primary server (or the first the bot reaches), default fields. */
export function newProductDraft(catalog: AdminProductCatalog): ProductDraft {
  const reachable = catalog.guilds.filter(isReachable)
  const guild = reachable.find((each) => each.primary) ?? reachable[0]
  return {
    addOns: [],
    currency: "USD",
    description: "",
    existingProductId: "",
    features: "",
    guildId: guild?.id ?? "",
    imageUrl: null,
    initialPayment: "",
    interval: "month",
    intervalCount: "1",
    name: "",
    noRoles: false,
    price: "",
    pricingType: "free",
    productType: "discord",
    removal: "kick",
    removalRoleIds: [],
    roleIds: [],
    signupFields: defaultSignupFields(),
    slug: "",
    slugEdited: false,
    stock: "99",
    trialDays: "0",
    trialRoleIds: [],
    upgradeTo: [],
  }
}

/** A saved free product as a draft. Its link stays as published when the name changes. */
export function draftFromFreeProduct(
  catalog: AdminProductCatalog,
  product: AdminFreeProduct,
): ProductDraft {
  return {
    ...newProductDraft(catalog),
    description: product.description,
    guildId: product.access.guildId,
    imageUrl: product.imageUrl,
    name: product.name,
    noRoles: product.access.roleIds.length === 0,
    roleIds: [...product.access.roleIds],
    signupFields: structuredClone(product.signupFields),
    slug: product.slug,
    slugEdited: true,
  }
}

/** A saved paid product as a draft. */
export function draftFromPaidProduct(
  catalog: AdminProductCatalog,
  product: AdminProduct,
): ProductDraft {
  const oneTime = product.interval === "one_time"
  const amount = (value: number) => (value / 100).toFixed(2)
  return {
    ...newProductDraft(catalog),
    addOns: [...product.addOns],
    currency: product.currency,
    features: product.features.join("\n"),
    initialPayment: product.initialPaymentMinor > 0 ? amount(product.initialPaymentMinor) : "",
    interval: oneTime ? "month" : (product.interval as ProductDraft["interval"]),
    intervalCount: String(product.intervalCount),
    name: product.name,
    price: amount(product.priceMinor),
    pricingType: oneTime ? "one_time" : "recurring",
    productType: product.productType,
    removal: product.removal,
    removalRoleIds: [...product.roleRemoval],
    roleIds: [...product.roles],
    slug: product.slug ?? "",
    slugEdited: true,
    stock: String(product.stock),
    trialDays: String(product.trialDays),
    trialRoleIds: [...product.trialRoles],
    upgradeTo: [...product.upgradeTo],
  }
}

/** The free product request: the whole record, since PUT replaces it. */
export function freeRequest(draft: ProductDraft): AdminCreateFreeProductRequest {
  return {
    access: { guildId: draft.guildId, roleIds: draft.noRoles ? [] : draft.roleIds },
    description: draft.description.trim(),
    imageUrl: draft.imageUrl,
    name: draft.name.trim(),
    pricing: { type: "free" },
    signupFields: draft.signupFields,
    slug: draft.slug,
  }
}

/**
 * The paid product request, in today's flat shape (§3.1: the pricing wrapper ships with
 * payments). A slug the admin never typed is not sent: on a paid product it is a storefront
 * password, which would hide the product.
 */
export function paidRequest(draft: ProductDraft): AdminCreateProductRequest {
  const recurring = draft.pricingType === "recurring"
  const discord = draft.productType === "discord"
  const trialDays = recurring ? whole(draft.trialDays) : 0
  const slug = draft.slug.trim()
  return {
    addOns: draft.addOns,
    currency: draft.currency,
    existingProductId: draft.existingProductId || null,
    features: lines(draft.features),
    initialPaymentMinor: recurring ? minor(draft.initialPayment) : 0,
    interval: recurring ? draft.interval : "one_time",
    intervalCount: recurring ? whole(draft.intervalCount) : 1,
    name: draft.name.trim(),
    priceMinor: minor(draft.price),
    productType: draft.productType,
    removal: draft.removal,
    roleRemoval: draft.removal === "roles" ? draft.removalRoleIds : [],
    roles: discord ? draft.roleIds : [],
    slug: draft.slugEdited && slug ? slug : null,
    stock: whole(draft.stock),
    trialDays,
    trialRoles: discord && trialDays > 0 ? draft.trialRoleIds : [],
    upgradeTo: draft.upgradeTo,
  }
}

// Each step's checks, by draft key. The wizard shows them on the step's fields.

export function validateBasics(draft: ProductDraft, takenSlugs: readonly string[]): WizardErrors {
  const errors: WizardErrors = {}
  const name = draft.name.trim()
  if (!name) errors.name = "Enter a product name."
  else if (name.length > 120) errors.name = "Use 120 characters or fewer."
  if (draft.pricingType === "free") {
    if (!draft.slug) errors.slug = "Enter a link for the signup page."
    else if (!signupSlugSchema.safeParse(draft.slug).success) {
      errors.slug = "Use only lowercase letters, numbers and hyphens (3 to 48)."
    } else if (takenSlugs.includes(draft.slug)) errors.slug = SLUG_TAKEN
    if (draft.description.trim().length > 2000) {
      errors.description = "Use 2,000 characters or fewer."
    }
  } else if (draft.slugEdited && draft.slug.trim() && !PAID_SLUG.test(draft.slug.trim())) {
    errors.slug = "Use letters, numbers, hyphens or underscores."
  }
  return errors
}

/**
 * A new paid product needs Stripe (R28), and a new free one needs `freeWritable`: a proven server
 * and an account that may write. An existing product keeps its type, so it is not asked.
 */
export function validatePricing(
  draft: ProductDraft,
  catalog: AdminProductCatalog,
  editing: boolean,
): WizardErrors {
  if (editing) return {}
  if (draft.pricingType === "free") {
    if (catalog.freeWritable) return {}
    return {
      pricingType:
        catalog.guilds.length === 0
          ? "Connect Discord before creating free products."
          : "This account can’t create free products.",
    }
  }
  return catalog.writable ? {} : { pricingType: "Connect Stripe before creating paid products." }
}

export function validatePrice(draft: ProductDraft): WizardErrors {
  if (!MONEY.test(draft.price.trim())) return { price: "Enter a price, such as 29 or 29.99." }
  return minor(draft.price) > 0 ? {} : { price: "Enter a price above 0, or choose Free." }
}

/**
 * A recurring product's period, initial payment and trial. Editing, the period and the initial
 * payment are locked (Stripe keeps them), so only the trial is checked.
 */
export function validateBilling(draft: ProductDraft, editing = false): WizardErrors {
  const errors: WizardErrors = {}
  if (editing) {
    if (!WHOLE.test(draft.trialDays.trim()) || whole(draft.trialDays) > 730) {
      errors.trialDays = "Enter 0 to 730 days."
    }
    return errors
  }
  if (!WHOLE.test(draft.intervalCount.trim()) || whole(draft.intervalCount) < 1) {
    errors.intervalCount = "Enter a whole number of 1 or more."
  }
  if (draft.initialPayment.trim() && !MONEY.test(draft.initialPayment.trim())) {
    errors.initialPayment = "Enter an amount, such as 10 or 9.99."
  }
  if (!WHOLE.test(draft.trialDays.trim()) || whole(draft.trialDays) > 730) {
    errors.trialDays = "Enter 0 to 730 days."
  }
  return errors
}

/**
 * `kept` is the saved product's own roles: as the API does (G4), a role the bot cannot give is
 * refused only when it is newly chosen, so an edit keeps a role that drifted above the bot.
 */
export function validateAccess(
  draft: ProductDraft,
  catalog: AdminProductCatalog,
  kept: string[] = [],
): WizardErrors {
  if (draft.pricingType !== "free") {
    const errors: WizardErrors = {}
    if (draft.productType === "discord" && draft.roleIds.length === 0) {
      errors.roleIds = "Choose at least one role."
    }
    if (draft.removal === "roles" && draft.removalRoleIds.length === 0) {
      errors.removalRoleIds = "Choose at least one role to remove."
    }
    return errors
  }
  if (catalog.guilds.length === 0) return { guildId: "Connect Discord before you continue." }
  const guild = catalog.guilds.find((each) => each.id === draft.guildId)
  if (!guild) return { guildId: "Choose one of your connected Discord servers." }
  // The API refuses a server its bot cannot reach (503), so the draft stops here instead.
  if (!isReachable(guild)) {
    return { guildId: "The bot can’t reach this server. Choose another, or reconnect it." }
  }
  if (draft.noRoles) return {}
  const outOfReach = (id: string) =>
    !kept.includes(id) && guild.roles.find((role) => role.id === id)?.assignable === false
  // The API's own words, so Continue and Publish say the same.
  if (draft.roleIds.some(outOfReach)) return { roleIds: "Choose roles below the Ternary role." }
  if (draft.roleIds.length > 0) return {}
  return {
    roleIds:
      guild.roles.length === 0
        ? "This server has no roles to grant. Choose Don’t grant any roles."
        : "Choose at least one role.",
  }
}

export function validateFields(draft: ProductDraft): WizardErrors {
  const result = signupFieldsSchema.safeParse(draft.signupFields)
  return result.success
    ? {}
    : { signupFields: result.error.issues[0]?.message ?? "Check the signup fields." }
}

export function validateExtras(draft: ProductDraft): WizardErrors {
  return WHOLE.test(draft.stock.trim()) ? {} : { stock: "Enter a whole number of 0 or more." }
}
